import {Injectable} from '@angular/core';
import {Subject, Subscription} from 'rxjs';

/**
 * Globalny system komunikacji oparty na wzorcu Publish-Subscribe (Event Bus).
 * Umożliwia asynchroniczną wymianę danych i zdarzeń między luźno powiązanymi
 * (loosely coupled) komponentami i serwisami w aplikacji, izolując je od siebie.
 */
@Injectable({
  providedIn: 'root'
})
export class EventsService {

  /** Słownik przechowujący aktywne kanały komunikacyjne (tematy).
   * Kluczem jest nazwa tematu (string), a wartością instancja RxJS Subject.
   */
  private channels: { [key: string]: Subject<any>; } = {};

  /**
   * Rejestruje nasłuchiwacza (Observer) na określony temat (Topic).
   * Jeśli dany kanał jeszcze nie istnieje, zostaje automatycznie utworzony.
   *
   * @param topic Unikalna nazwa kanału zdarzeń.
   * @param observer Funkcja zwrotna (Callback) wywoływana w momencie nadejścia nowych danych.
   * @returns Obiekt Subscription pozwalający na późniejsze anulowanie subskrypcji i zapobieganie wyciekom pamięci.
   */
  subscribe(topic: string, observer: (_: any) => void): Subscription {
    if (!this.channels[topic]) {
      this.channels[topic] = new Subject<any>();
    }

    return this.channels[topic].subscribe(observer);
  }

  /**
   * Rozgłasza nowe zdarzenie (Payload) do wszystkich aktywnych subskrybentów danego kanału.
   * Jeśli kanał nie istnieje (nikt go nie nasłuchuje), operacja jest cicho ignorowana.
   *
   * @param topic Nazwa kanału docelowego.
   * @param data Ładunek danych do przekazania subskrybentom.
   */
  publish(topic: string, data: any): void {
    const subject = this.channels[topic];
    if (!subject) {
      return;
    }

    subject.next(data);
  }

  /**
   * Trwale niszczy określony kanał komunikacyjny, kończąc strumień i zwalniając pamięć.
   *
   * @param topic Nazwa kanału do usunięcia.
   * @returns Null w ramach domyślnego przepływu kontroli.
   */
  destroy(topic: string): null {
    const subject = this.channels[topic];
    if (!subject) {
      return;
    }

    subject.complete();
    delete this.channels[topic];

    return null;
  }
}
